import React from 'react';
import { HelpButton } from '@/components/ui/help-button';
import { fieldDescriptions } from '../utils/loadDescriptions';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { useShortTurnConfig } from '../ShortTurnConfigForm';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { buildSurchargeScopeInjectable } from '../utils/surchargeScope';
import { cn } from '@/lib/utils';
import { LabeledToggle } from '@/components/config_forms/shared/LabeledToggle';

interface SurchargePeriodFieldProps {
  disabled?: boolean;
}

export const SurchargePeriodField: React.FC<SurchargePeriodFieldProps> = ({ disabled = false }) => {
  const {
    shortTurnConfig,
    onShortTurnConfigChange,
    customizedFields = {},
    context = 'user',
    markdownVariables,
    rateMultipliers,
    roles,
  } = useShortTurnConfig();

  const scopeInjectable = React.useMemo(() => {
    return buildSurchargeScopeInjectable({
      rateMultipliers,
      shortTurnConfig,
      roles,
      onScopeChange: (shortedHoursOnly) =>
        onShortTurnConfigChange({ surchargeAppliesToShortedHoursOnly: shortedHoursOnly }),
    });
  }, [rateMultipliers, shortTurnConfig, roles, onShortTurnConfigChange]);

  const shortedHoursOnly = shortTurnConfig.surchargeAppliesToShortedHoursOnly ?? false;

  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center sm:flex sm:items-center sm:gap-4',
        disabled && 'opacity-55'
      )}
      aria-disabled={disabled}
    >
      <div className="min-w-0 sm:space-y-1 sm:flex-1">
        <div className="flex items-center gap-2">
          <span className={fieldLabelClass}>Surcharge Period</span>
          <CustomizedFieldIndicator isCustomized={customizedFields.surcharge_applies_to_shorted_hours_only} context={context} />
          <HelpButton
            fieldName="Short Turn - Surcharge Period"
            content={fieldDescriptions.surcharge_period}
            variables={markdownVariables}
            enableFieldFocus
            injectableComponents={scopeInjectable.injectables}
          />
        </div>
        <p className={cn(fieldDescriptionClass, "hidden sm:block md:max-w-[44ch]")}>
          Choose whether the surcharge applies to the entire shift or only the portion that was shorted.
        </p>
      </div>
      <div className="flex justify-end sm:flex-shrink-0">
        <LabeledToggle
          ariaLabel="Surcharge Period"
          leftLabel="Shorted Hours"
          rightLabel="Full Shift"
          rightSelected={!shortedHoursOnly}
          onChange={(rightSelected) => onShortTurnConfigChange({ surchargeAppliesToShortedHoursOnly: !rightSelected })}
          disabled={disabled}
        />
      </div>
      <p className={cn(fieldDescriptionClass, "col-span-2 sm:hidden")}>
        Choose whether the surcharge applies to the entire shift or only the portion that was shorted.
      </p>
    </div>
  );
};
